/**
 * @class ProductOfferBundle
 */
if (!customElements.get('product-offer-bundle')) {
  class ProductOfferBundle extends HTMLElement {
    constructor() {
      super();

      this.handleVariantChangeBound = this.handleVariantChange.bind(this);
      this.handleAddToCartBound = this.handleAddToCart.bind(this);
    }

    connectedCallback() {
      this.regularPriceEl = this.querySelector('[data-offer-regular-price]');
      this.bundlePriceEl = this.querySelector('[data-offer-bundle-price]');
      this.savingsEl = this.querySelector('[data-offer-savings]');
      this.savingsCol = this.querySelector('[data-offer-savings-col]');
      this.addButton = this.querySelector('[data-offer-add-to-cart]');
      this.offerItems = this.querySelectorAll('[data-offer-item]');
      this.currentProductId = this.dataset.currentProductId;
      this.bundleVariantId = this.dataset.bundleVariantId;

      this.addButton?.addEventListener('click', this.handleAddToCartBound);
      document.addEventListener('product:variant-change', this.handleVariantChangeBound);
    }

    disconnectedCallback() {
      this.addButton?.removeEventListener('click', this.handleAddToCartBound);
      document.removeEventListener('product:variant-change', this.handleVariantChangeBound);
    }

    getMoneyFormat() {
      return window.theme?.settings?.money_with_currency_format || window.theme?.settings?.money_format;
    }

    formatPrice(cents) {
      if (typeof formatMoney === 'function') {
        return formatMoney(cents, this.getMoneyFormat());
      }
      return (cents / 100).toFixed(2);
    }

    getRegularPrice() {
      let total = 0;

      this.offerItems.forEach((item) => {
        total += parseInt(item.dataset.variantPrice, 10) || 0;
      });

      return total;
    }

    updatePricing() {
      const regularPrice = this.getRegularPrice();
      const bundlePrice = parseInt(this.dataset.bundlePrice, 10) || 0;
      const savings = regularPrice - bundlePrice;

      this.dataset.regularPrice = regularPrice;
      this.dataset.savings = savings;

      if (this.regularPriceEl) {
        this.regularPriceEl.textContent = this.formatPrice(regularPrice);
      }

      if (this.savingsEl) {
        this.savingsEl.textContent = this.formatPrice(Math.max(savings, 0));
      }

      if (this.savingsCol) {
        const showSavings = savings > 0;
        this.savingsCol.hidden = !showSavings;
        this.savingsCol.classList.toggle('visually-hidden', !showSavings);
      }
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      let updated = false;

      this.offerItems.forEach((item) => {
        if (item.dataset.productId !== this.currentProductId) return;

        item.dataset.variantId = variant.id;
        item.dataset.variantPrice = variant.price;
        updated = true;
      });

      if (updated) {
        this.updatePricing();
      }
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        if (!container) return;

        const elementToReplace = container.querySelector(section.selector) || container;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.notesToggle();
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }

        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      });
    }

    handleAddToCart() {
      if (!this.bundleVariantId || !this.addButton || this.addButton.disabled) return;

      this.addButton.classList.add('loading');
      this.addButton.setAttribute('aria-disabled', 'true');

      const formData = new FormData();
      formData.append('id', this.bundleVariantId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      fetch(`${theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript'
        },
        body: formData
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }

          this.renderContents(response);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', {
              product: Object.prototype.hasOwnProperty.call(response, 'items') ? response.items[0] : response
            });
          }

          const cartDrawer = document.getElementById('Cart-Drawer');
          if (cartDrawer) {
            document.body.classList.add('open-cc');
            document.body.classList.add('open-cart');
            cartDrawer.classList.add('active');
            if (typeof dispatchCustomEvent === 'function') {
              dispatchCustomEvent('cart-drawer:open');
            }
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.addButton.classList.remove('loading');
          this.addButton.removeAttribute('aria-disabled');
        });
    }
  }

  customElements.define('product-offer-bundle', ProductOfferBundle);
}
